import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FiArrowLeft,
  FiShare2,
  FiStar,
  FiMic,
  FiActivity,
  FiRefreshCw,
  FiTarget,
  FiTrendingUp,
  FiBookOpen,
  FiEdit3,
  FiBarChart,
  FiMessageSquare,
  FiMusic,
  FiHelpCircle,
  FiInfo,
  FiAward,
  FiCheckCircle,
  FiAlertCircle,
  FiMap,
  FiClock,
  FiCalendar,
  FiX,
  FiVolume2,
} from 'react-icons/fi';
import ReactMarkdown from 'react-markdown';
import { sanitizeSpeakingFeedbackMarkdown } from '../../../utils/speakingFeedbackMarkdown';
import { extractPronounceLemma } from '../../../utils/pronounceLemma';
import { renderNuanceInlineMarkdown } from '../../../utils/renderNuanceInlineMarkdown';
import Sidebar from '../../Sidebar';
import GetStartedWith from '../../../components/GetStartedWith';
import './SpeakingResults.css';
import {
  API_BASE_URL,
  getAccountScopedLocalStorageKey,
  prefetchWordPronunciations,
  pronounceWord,
  getAuthHeaders,
} from '../../../utils/utils';
import { getStoredUser } from '../../../utils/authSession';
import { invalidateHomeMandatorySnapshot } from '../../home/homeSnapshot';
import ToeflNextStepCard from '../toefl/ToeflNextStepCard';
import {
  getCEFRFromIELTS,
  getAdmissionInfo,
  formatCEFRDisplay,
  getNextCEFRLevel,
  getDaysToTarget,
} from '../../../utils/cefrUtils';
import {
  generateJourneyPlan,
  getOrCreateJourney,
  saveJourneyProgress,
} from '../../../utils/languageJourneyData';
import { updateIeltsProfile } from '../../../utils/ieltsProfile';
const serverUrl = API_BASE_URL;
const SPELLING_USUK_LESSON_PATH = '/exams/english/vocabulary/spelling-us-uk';

function spellingMixFromSources(...sources) {
  for (const source of sources) {
    const value = source?.spellingVariant || source?.spelling_variant;
    if (value && value.variant === 'mixed') return value;
  }
  return null;
}

function SpellingMixBanner({ variant, lessonPath = SPELLING_USUK_LESSON_PATH }) {
  if (!variant || variant.variant !== 'mixed') return null;
  const pairs = [
    ...new Set((variant.pairs || []).map((item) => `${item.british} / ${item.american}`)),
  ];
  return (
    <div
      data-testid="spelling-mix-banner"
      style={{
        margin: '1rem 0',
        padding: '0.85rem 1rem',
        background: '#fff8e6',
        border: '1px solid #f0d48a',
        borderRadius: 8,
      }}
    >
      <p style={{ margin: '0 0 0.4rem' }}>
        You mixed British and American spelling
        {pairs.length ? ` (${pairs.join(', ')})` : ''}. IELTS accepts either
        system. Pick one and use it for the whole test.
      </p>
      <a href={lessonPath}>Open the US vs UK spelling lesson</a>
    </div>
  );
}

const getScopedLocalStorageItem = (key) => {
  const scopedKey = getAccountScopedLocalStorageKey(key);
  return scopedKey ? localStorage.getItem(scopedKey) : null;
};

const SpeakingResults = () => {
  const { testId } = useParams();
  const navigate = useNavigate();
  const [resultData, setResultData] = useState(null);
  const [allAttempts, setAllAttempts] = useState([]);
  const [selectedAttemptIndex, setSelectedAttemptIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showEnrollmentModal, setShowEnrollmentModal] = useState(false);
  const [, setIsFirstTest] = useState(false); // eslint-disable-line no-unused-vars
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [showLevelUpdateModal, setShowLevelUpdateModal] = useState(false);
  const [levelUpdateInfo, setLevelUpdateInfo] = useState(null);
  const [showRetakeConfirmModal, setShowRetakeConfirmModal] = useState(false);
  const [resolvedPronunciations, setResolvedPronunciations] = useState({});

  // Check enrollment status
  const checkEnrollmentStatus = () => {
    try {
      const user = getStoredUser();
      if (!user?.email) return false;
      const journeyData = localStorage.getItem(`languageJourney_${user.email}`);
      if (journeyData) {
        const parsed = JSON.parse(journeyData);
        return parsed.enrolled === true;
      }
      return false;
    } catch {
      return false;
    }
  };

  // Get the next CEFR level (B1 → B2, B2 → C1, etc.)
  const getNextLevel = (level) => {
    const levels = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];
    const currentIndex = levels.indexOf(level);
    if (currentIndex === -1 || currentIndex >= levels.length - 1) return level;
    return levels[currentIndex + 1];
  };

  // Handle enrollment
  // IMPORTANT: Always checks backend first to ensure cross-device consistency
  const handleEnroll = async () => {
    try {
      const user = getStoredUser();
      if (!user?.email) return;

      const assessment = getAssessment(resultData?.results);
      const overallScore = assessment?.overallBandScore || 5;
      const cefrLevel = getCEFRFromIELTS(overallScore);

      // Target is the NEXT level (B1 → B2, B2 → C1)
      const targetLevel = getNextLevel(cefrLevel.code);

      // Use getOrCreateJourney to check backend first before creating new journey
      // This ensures cross-device consistency
      await getOrCreateJourney(
        user.email,
        cefrLevel.code,
        targetLevel,
        overallScore
      );

      setIsEnrolled(true);
      setShowEnrollmentModal(false);
      invalidateHomeMandatorySnapshot(user.email);
      navigate('/exams/english/journey');
    } catch (error) {
      console.error('Error enrolling in journey:', error);
    }
  };

  // Handle level update for already enrolled users (reassessment)
  const handleLevelUpdate = async () => {
    try {
      const user = getStoredUser();
      if (!user?.email || !levelUpdateInfo) return;

      const { newLevel, newScore } = levelUpdateInfo;
      const targetLevel = getNextLevel(newLevel);
      const updatedAt = new Date();

      // Generate a new journey from the new level (with user email for deterministic seeding)
      const journeyPlan = generateJourneyPlan(
        newLevel,
        targetLevel,
        updatedAt,
        user.email
      );
      journeyPlan[0].status = 'current';

      const journeyData = {
        enrolled: true,
        currentLevel: newLevel,
        targetLevel: targetLevel,
        startDate: updatedAt.toISOString(),
        currentDay: 1,
        assessmentScore: newScore,
        journey: journeyPlan,
        levelUpdatedFromTest: true,
        previousLevel: levelUpdateInfo.currentLevel,
      };

      // Use saveJourneyProgress to ensure backend sync
      await saveJourneyProgress(user.email, journeyData);
      try {
        await updateIeltsProfile({
          current_level: newLevel,
          target_level: targetLevel,
          enrolled: true,
        });
      } catch (error) {
        console.warn('Failed to persist IELTS profile after level update', error);
      }

      setShowLevelUpdateModal(false);
      navigate('/exams/english/journey');
    } catch (error) {
      console.error('Error updating journey level:', error);
    }
  };

  // Check if level update is available (for reassessments)
  const checkForLevelUpdate = (currentResults) => {
    try {
      const user = getStoredUser();
      if (!user?.email) return;

      const journeyDataStr = localStorage.getItem(
        `languageJourney_${user.email}`
      );
      if (!journeyDataStr) return;

      const journeyData = JSON.parse(journeyDataStr);
      if (!journeyData.enrolled) return;

      const assessment = getAssessment(currentResults?.results);
      const newScore = assessment?.overallBandScore || 5;
      const newLevel = getCEFRFromIELTS(newScore);
      const currentLevel = journeyData.currentLevel;

      // Compare CEFR levels
      const levels = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];
      const newLevelIndex = levels.indexOf(newLevel.code);
      const currentLevelIndex = levels.indexOf(currentLevel);

      // If new level is different from current journey level, offer update
      if (newLevelIndex !== currentLevelIndex) {
        setLevelUpdateInfo({
          currentLevel: currentLevel,
          newLevel: newLevel.code,
          newScore: newScore,
          isUpgrade: newLevelIndex > currentLevelIndex,
        });
        // Show modal after a delay
        setTimeout(() => setShowLevelUpdateModal(true), 2000);
      }
    } catch (error) {
      console.error('Error checking for level update:', error);
    }
  };

  // Skip enrollment for now
  const handleSkipEnrollment = () => {
    setShowEnrollmentModal(false);
  };

  useEffect(() => {
    loadResultData();
  }, [testId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check enrollment and first test status after loading
  useEffect(() => {
    if (!loading && resultData) {
      const enrolled = checkEnrollmentStatus();
      setIsEnrolled(enrolled);

      // If not enrolled and we have results, offer enrollment after viewing results
      if (!enrolled) {
        if (allAttempts.length === 1) {
          setIsFirstTest(true);
        }
        // Show enrollment modal after a short delay for non-enrolled users
        setTimeout(() => setShowEnrollmentModal(true), 2000);
      } else {
        // User is enrolled - check if their new assessment warrants a level update
        checkForLevelUpdate(resultData);
      }
    }
  }, [loading, resultData, allAttempts]); // eslint-disable-line react-hooks/exhaustive-deps

  // Helper function to map backend result to frontend structure
  const mapBackendResult = (backendResult) => {
    // The assessment_data may or may not include problematicWords/missedWordsActions.
    // They could be stored as top-level fields in the backend response (snake_case or camelCase).
    const assessmentData =
      backendResult.assessment_data || backendResult.results || {};

    // Extract problematicWords from all possible locations
    const problematicWords =
      assessmentData.problematicWords ||
      backendResult.problematicWords ||
      backendResult.problematic_words ||
      [];

    // Extract missedWordsActions from all possible locations
    const missedWordsActions =
      assessmentData.missedWordsActions ||
      backendResult.missedWordsActions ||
      backendResult.missed_words_actions ||
      [];

    // Extract examinerDebrief from all possible locations
    const examinerDebrief =
      assessmentData.examinerDebrief ||
      backendResult.examinerDebrief ||
      backendResult.examiner_debrief ||
      null;

    // Merge problematicWords/missedWordsActions into results so helpers find them
    const results =
      typeof assessmentData === 'object'
        ? {
            ...assessmentData,
            problematicWords,
            missedWordsActions,
            examinerDebrief,
          }
        : assessmentData;

    return {
      testId: backendResult.test_id,
      testType: backendResult.test_type,
      userEmail: backendResult.user_email,
      topic: backendResult.topic,
      topicCategory:
        backendResult.topic_category || backendResult.category || 'General',
      testTitle: backendResult.test_title || 'Speaking Practice',
      completedAt: backendResult.created_at,
      startTime: backendResult.start_time,
      endTime: backendResult.end_time,
      duration: backendResult.time_taken_seconds,
      results,
      transcript: backendResult.transcript || [],
      backendResult: backendResult, // Store the full backend response
      // IELTS test specific data
      isIELTSTest: backendResult.is_ielts_test || false,
      isExamMode:
        backendResult.isExamMode || backendResult.is_exam_mode || false,
      ieltsTestData: backendResult.ielts_test_data || null,
    };
  };

  const loadResultData = async () => {
    try {
      setLoading(true);

      // First try to get results from backend
      try {
        const user = getStoredUser();
        if (!user?.email) {
          throw new Error('User email not found');
        }

        console.log('Fetching speaking results from backend...');
        const response = await fetch(
          `${serverUrl}/api/get-speaking-test-results?testType=speaking&testId=${testId}`,
          { headers: getAuthHeaders() }
        );

        if (response.ok) {
          const backendData = await response.json();
          console.log('Backend speaking results:', backendData);

          if (
            backendData.success &&
            backendData.results &&
            backendData.results.length > 0
          ) {
            // Map all attempts to our expected structure
            const mappedAttempts = backendData.results.map((result) =>
              mapBackendResult(result)
            );

            // Sort by completion date (newest first)
            mappedAttempts.sort(
              (a, b) => new Date(b.completedAt) - new Date(a.completedAt)
            );

            setAllAttempts(mappedAttempts);

            // Find the current test result or use the latest
            let currentIndex = mappedAttempts.findIndex(
              (attempt) => attempt.testId.toString() === testId
            );

            if (currentIndex === -1) {
              // If specific testId not found, use the latest attempt
              currentIndex = 0;
            }

            setSelectedAttemptIndex(currentIndex);
            setResultData(mappedAttempts[currentIndex]);

            setLoading(false);
            return;
          } else {
            throw new Error('No results found in backend response');
          }
        } else {
          throw new Error(`Backend request failed: ${response.status}`);
        }
      } catch (backendError) {
        console.log(
          'Backend results not found, checking localStorage...',
          backendError
        );

        // Fallback to localStorage for recently completed tests
        const userProgress = JSON.parse(
          getScopedLocalStorageItem('userProgress') || '[]'
        );
        console.log('localStorage userProgress:', userProgress, testId);

        // Find the current result
        const result = userProgress.find(
          (item) => item.testId.toString() === testId
        );

        if (result) {
          // Find all attempts for the same test topic (for comparison)
          const sameTopicAttempts = userProgress
            .filter(
              (item) =>
                item.testType === 'speaking' &&
                (item.topic === result.topic ||
                  item.testTitle === result.testTitle)
            )
            .sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt));

          setAllAttempts(sameTopicAttempts);

          // Find the index of current result in sorted attempts
          const currentIndex = sameTopicAttempts.findIndex(
            (attempt) => attempt.testId.toString() === testId
          );

          setSelectedAttemptIndex(currentIndex >= 0 ? currentIndex : 0);
          setResultData(
            sameTopicAttempts[currentIndex >= 0 ? currentIndex : 0]
          );
        } else {
          console.error('Result not found in both backend and localStorage');
        }
      }
    } catch (error) {
      console.error('Error loading result data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAttemptChange = (index) => {
    setSelectedAttemptIndex(index);
    setResultData(allAttempts[index]);
  };

  // Show retake confirmation modal
  const handleRetakeClick = () => {
    // If there are more than 1 attempts, user has already done at least one retake
    // The button should already be hidden in this case, but this is a safeguard
    if (allAttempts.length > 1) {
      return;
    }
    setShowRetakeConfirmModal(true);
  };

  // Confirm and proceed with retake
  const handleRetakeConfirm = () => {
    setShowRetakeConfirmModal(false);
    // Navigate to speaking test with the same topic and category
    navigate('/exams/english/speaking', {
      state: {
        topic: resultData.topic,
        topicCategory: resultData.topicCategory || 'General',
        fromResults: true,
        retakeMode: true,
        // If it was an IELTS test, trigger IELTS test mode again
        isCheckpoint: resultData.isIELTSTest || false,
        fromJourney: resultData.isIELTSTest || false,
      },
    });
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: 'My IELTS Speaking Results',
        text: `I just completed an IELTS speaking practice with Liwaza AI! Overall score: ${resultData.results.assessment.overallBandScore}/9`,
        url: window.location.href,
      });
    } else {
      const shareText = `I just completed an IELTS speaking practice with Liwaza AI! Overall score: ${resultData.results.assessment.overallBandScore}/9\n\nCheck out Liwaza AI for IELTS preparation: ${window.location.origin}`;
      navigator.clipboard.writeText(shareText);
      alert('Results copied to clipboard!');
    }
  };

  const handleNavigateToSpeaking = () => {
    navigate('/exams/english/speaking');
  };

  // eslint-disable-next-line no-unused-vars
  const handleUpgradeSubscription = () => {
    navigate('/pricing');
  };

  const handleViewProgress = () => {
    navigate('/exams/english/progression', {
      state: { activeCategory: 'progression' },
    });
  };

  const formatDate = (isoString) => {
    return new Date(isoString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  // Score color helper based on band score
  const getScoreColor = (score) => {
    if (score >= 7) return 'score-excellent';
    if (score >= 5.5) return 'score-good';
    return 'score-needs-improvement';
  };

  // Helper function to safely get assessment data
  const getAssessment = (results) => {
    if (!results) return null;

    // Handle different data structures
    if (results.assessment) {
      return results.assessment;
    } else if (results.overallBandScore !== undefined) {
      // Direct structure without nested assessment
      return {
        overallBandScore: results.overallBandScore,
        fluencyAndCoherence: results.fluencyAndCoherence,
        lexicalResource: results.lexicalResource,
        grammaticalRangeAndAccuracy: results.grammaticalRangeAndAccuracy,
        pronunciation: results.pronunciation,
      };
    }

    return null;
  };

  // Helper function to safely get areas for improvement
  const getAreasForImprovement = (results) => {
    if (!results) return [];

    const rawItems =
      results.areasForImprovement ||
      resultData.backendResult?.areasForImprovement;

    if (Array.isArray(rawItems) && rawItems.length > 0) {
      return rawItems.map((area) => sanitizeSpeakingFeedbackMarkdown(area));
    }

    return ['Continue practicing to improve your speaking skills'];
  };

  // Helper function to safely get encouragement
  const getEncouragement = (results) => {
    if (!results) return 'Keep practicing with Liwaza AI!';

    if (results.encouragement) {
      return results.encouragement;
    } else if (resultData.backendResult?.encouragement) {
      return resultData.backendResult.encouragement;
    }

    return 'Keep practicing with Liwaza AI!';
  };

  // Helper function to safely get daily practice message
  const getDailyPracticeMessage = (results) => {
    if (!results) return 'Come back daily for more practice!';

    if (results.dailyPracticeMessage) {
      return results.dailyPracticeMessage;
    } else if (resultData.backendResult?.dailyPracticeMessage) {
      return resultData.backendResult.dailyPracticeMessage;
    }

    return 'Come back daily for more practice!';
  };

  // Helper function to safely get problematic words (checks camelCase & snake_case)
  const getProblematicWords = (results) => {
    if (!results) return [];
    // Check results object (camelCase - frontend format)
    if (results.problematicWords?.length) return results.problematicWords;
    // Check results object (snake_case - backend format)
    if (results.problematic_words?.length) return results.problematic_words;
    // Check backendResult (camelCase)
    if (resultData?.backendResult?.problematicWords?.length)
      return resultData.backendResult.problematicWords;
    // Check backendResult (snake_case)
    if (resultData?.backendResult?.problematic_words?.length)
      return resultData.backendResult.problematic_words;
    // Check inside assessment_data of backendResult
    if (resultData?.backendResult?.assessment_data?.problematicWords?.length)
      return resultData.backendResult.assessment_data.problematicWords;
    return [];
  };

  // Helper function to safely get missed words actions (checks camelCase & snake_case)
  const getMissedWordsActions = (results) => {
    if (!results) return [];
    // Check results object (camelCase - frontend format)
    if (results.missedWordsActions?.length) return results.missedWordsActions;
    // Check results object (snake_case - backend format)
    if (results.missed_words_actions?.length)
      return results.missed_words_actions;
    // Check backendResult (camelCase)
    if (resultData?.backendResult?.missedWordsActions?.length)
      return resultData.backendResult.missedWordsActions;
    // Check backendResult (snake_case)
    if (resultData?.backendResult?.missed_words_actions?.length)
      return resultData.backendResult.missed_words_actions;
    // Check inside assessment_data of backendResult
    if (resultData?.backendResult?.assessment_data?.missedWordsActions?.length)
      return resultData.backendResult.assessment_data.missedWordsActions;
    return [];
  };

  const speakingWordList = useMemo(() => {
    const words = getProblematicWords(resultData?.results);
    const actions = getMissedWordsActions(resultData?.results);
    return [
      ...words.map((item) => item?.word),
      ...actions.map((item) => item?.word),
    ];
  }, [resultData]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let cancelled = false;
    if (!speakingWordList.length) return undefined;
    prefetchWordPronunciations(speakingWordList).then((data) => {
      if (!cancelled) setResolvedPronunciations(data || {});
    });
    return () => {
      cancelled = true;
    };
  }, [speakingWordList]);

  const playSpeakingWord = (rawWord) => {
    const lemma = extractPronounceLemma(rawWord);
    const resolved = resolvedPronunciations[lemma.toLowerCase()];
    pronounceWord(lemma || rawWord, resolved?.audio_url);
  };

  const resolvedSpeakingPhonetic = (wordItem) => {
    const lemma = extractPronounceLemma(wordItem?.word);
    return (
      resolvedPronunciations[lemma.toLowerCase()]?.phonetic ||
      wordItem?.phonetic
    );
  };

  // Helper function to safely get examiner debrief (exam mode only, checks camelCase & snake_case)
  const getExaminerDebrief = (results) => {
    if (!results) return null;
    if (results.examinerDebrief) return results.examinerDebrief;
    if (results.examiner_debrief) return results.examiner_debrief;
    if (resultData?.backendResult?.examinerDebrief)
      return resultData.backendResult.examinerDebrief;
    if (resultData?.backendResult?.examiner_debrief)
      return resultData.backendResult.examiner_debrief;
    if (resultData?.backendResult?.assessment_data?.examinerDebrief)
      return resultData.backendResult.assessment_data.examinerDebrief;
    return null;
  };

  // Helper function to check if this was exam mode
  const getIsExamMode = () => {
    if (resultData?.isExamMode !== undefined) return resultData.isExamMode;
    if (resultData?.backendResult?.isExamMode !== undefined)
      return resultData.backendResult.isExamMode;
    if (results?.isExamMode !== undefined) return results.isExamMode;
    // Fall back to isIELTSTest
    return resultData?.isIELTSTest || false;
  };

  if (loading) {
    return (
      <div className="layout">
        <Sidebar />
        <div className="content">
          <div className="speaking-result-container">
            <div className="speaking-result-loading-state">
              <div className="speaking-result-loading-content">
                <div className="speaking-result-loading-spinner"></div>
                <p>Loading your results...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!resultData) {
    return (
      <div className="layout">
        <Sidebar />
        <div className="content">
          <div className="speaking-result-container">
            <div className="speaking-result-error-state">
              <h2>Result Not Found</h2>
              <p>The requested speaking test result could not be found.</p>
              <button
                className="btn-lwz-primary"
                onClick={() => navigate('/exams/english/speaking')}
              >
                Back to Speaking Tests
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const {
    results,
    topic,
    completedAt,
    duration,
    testTitle,
    isIELTSTest,
    ieltsTestData,
  } = resultData;
  const assessment = getAssessment(results);

  return (
    <div className="layout">
      <Sidebar />
      <div className="content">
        <div className="speaking-result-container">
          {/* Header Card with integrated controls */}
          <div className="speaking-result-header-card">
            <div className="speaking-result-card-content">
              {/* Back Button */}
              <div className="speaking-result-header-controls">
                <button
                  onClick={() => navigate(-1)}
                  className="speaking-result-back-button small"
                >
                  <FiArrowLeft />
                  <span>Back</span>
                </button>
              </div>

              <h1 className="speaking-result-card-title">
                <FiTarget style={{ marginRight: '0.5rem' }} /> IELTS Speaking
                Results
              </h1>
              <SpellingMixBanner
                variant={spellingMixFromSources(
                  results,
                  resultData,
                  resultData.backendResult,
                  resultData.backendResult?.assessment_data,
                )}
              />

              {/* Main Content - Left/Right Layout */}
              <div className="speaking-result-card-main-content">
                {/* Left Section - Controls */}
                <div className="speaking-result-card-left-section">
                  <div className="speaking-result-test-info">
                    <h2 className="speaking-result-test-title">{testTitle}</h2>
                    {!isIELTSTest && (
                      <p className="speaking-result-test-topic">
                        Topic: {topic}
                      </p>
                    )}
                    <div className="speaking-result-test-meta">
                      <span>Completed: {formatDate(completedAt)}</span>
                      <span>•</span>
                      <span>Duration: {formatDuration(duration)}</span>
                    </div>

                    {/* IELTS Test Format Badge */}
                    {isIELTSTest && (
                      <div className="ielts-test-format-badge">
                        <FiAward />
                        <span>Official IELTS-style 3-Part Assessment</span>
                      </div>
                    )}
                  </div>

                  {/* IELTS Test Parts Breakdown */}
                  {isIELTSTest && ieltsTestData && (
                    <div className="ielts-parts-breakdown">
                      <h3 className="ielts-parts-title">
                        <FiMic /> Test Parts Covered
                      </h3>
                      <div className="ielts-parts-grid">
                        {/* Part 1 */}
                        <div className="ielts-part-result-card">
                          <div className="ielts-part-result-header">
                            <span className="ielts-part-number">Part 1</span>
                            <span className="ielts-part-duration">4 min</span>
                          </div>
                          <h4>Introduction & Interview</h4>
                          {ieltsTestData.part1Topic && (
                            <p className="ielts-part-topic">
                              Topic: {ieltsTestData.part1Topic.topic}
                            </p>
                          )}
                        </div>

                        {/* Part 2 */}
                        <div className="ielts-part-result-card">
                          <div className="ielts-part-result-header">
                            <span className="ielts-part-number">Part 2</span>
                            <span className="ielts-part-duration">3-4 min</span>
                          </div>
                          <h4>Individual Long Turn</h4>
                          {ieltsTestData.part2CueCard && (
                            <div className="ielts-cue-card-summary">
                              <p className="ielts-part-topic">
                                {ieltsTestData.part2CueCard.topic}
                              </p>
                            </div>
                          )}
                        </div>

                        {/* Part 3 */}
                        <div className="ielts-part-result-card">
                          <div className="ielts-part-result-header">
                            <span className="ielts-part-number">Part 3</span>
                            <span className="ielts-part-duration">4 min</span>
                          </div>
                          <h4>Two-Way Discussion</h4>
                          {ieltsTestData.part3Questions && (
                            <p className="ielts-part-topic">
                              Theme: {ieltsTestData.part3Questions.theme}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Test Attempt Selector */}
                  {allAttempts.length > 1 && (
                    <div className="speaking-result-attempt-selector">
                      <label className="speaking-result-attempt-label">
                        Test Attempt:
                      </label>
                      <select
                        value={selectedAttemptIndex}
                        onChange={(e) =>
                          handleAttemptChange(parseInt(e.target.value))
                        }
                        className="speaking-result-attempt-select"
                      >
                        {allAttempts.map((attempt, index) => {
                          const attemptAssessment = getAssessment(
                            attempt.results
                          );
                          return (
                            <option key={index} value={index}>
                              Attempt {allAttempts.length - index} -{' '}
                              {new Date(
                                attempt.completedAt
                              ).toLocaleDateString()}
                              ({attemptAssessment?.overallBandScore || 'N/A'}/9)
                            </option>
                          );
                        })}
                      </select>
                      <div className="speaking-result-attempt-badge">
                        {selectedAttemptIndex === 0 ? 'Latest' : 'Previous'}
                      </div>
                    </div>
                  )}

                  {/* Retake Button */}
                  {allAttempts.length > 1 ? (
                    <div className="speaking-result-retake-limit">
                      <FiAlertCircle />
                      <span>You've already retaken this test once</span>
                    </div>
                  ) : (
                    <button
                      onClick={handleRetakeClick}
                      className="speaking-result-retake-button btn-lwz-primary"
                    >
                      <FiRefreshCw />
                      <span>
                        {isIELTSTest
                          ? 'Retake IELTS Speaking Test'
                          : 'Retake This Test'}
                      </span>
                    </button>
                  )}
                </div>

                {/* Right Section - Overall Score */}
                <div className="speaking-result-card-right-section">
                  <div className="speaking-result-score-card">
                    <h3>
                      Overall Band Score
                      <span
                        className="speaking-result-score-info-tooltip"
                        title="Overall IELTS Band Score (1-9): Liwaza AI analyzes your speaking performance across all criteria to provide a comprehensive assessment. This score represents your overall speaking ability based on fluency, vocabulary, grammar, and pronunciation."
                      >
                        <FiHelpCircle
                          style={{
                            marginLeft: '0.5rem',
                            cursor: 'pointer',
                            opacity: 0.7,
                          }}
                        />
                      </span>
                    </h3>
                    <div
                      className={`speaking-result-score-display ${getScoreColor(assessment?.overallBandScore)}`}
                    >
                      {assessment?.overallBandScore}
                      <span className="speaking-result-score-denominator">
                        /9
                      </span>
                    </div>
                    <div className="speaking-result-score-description">
                      {assessment?.overallBandScore >= 7
                        ? 'Excellent performance!'
                        : assessment?.overallBandScore >= 5.5
                          ? 'Good foundation to build on!'
                          : "Keep practicing - you're improving!"}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* CEFR Level Card */}
          {(() => {
            const cefrLevel = getCEFRFromIELTS(assessment?.overallBandScore);
            const admissionInfo = getAdmissionInfo(cefrLevel.code);
            const nextLevel = getNextCEFRLevel(cefrLevel.code);
            const daysToC1 = getDaysToTarget(cefrLevel.code, 'C1');

            return (
              <div className="speaking-result-cefr-card">
                <div className="speaking-result-cefr-card-header">
                  <h3>
                    <FiAward style={{ marginRight: '0.5rem' }} />
                    Your English Level (CEFR)
                    <span
                      className="speaking-result-score-info-tooltip"
                      title="CEFR (Common European Framework of Reference) is an international standard for describing language ability. Levels range from A1 (beginner) to C2 (proficient). Most graduate programs require B2-C1 level."
                    >
                      <FiHelpCircle
                        style={{
                          marginLeft: '0.5rem',
                          cursor: 'pointer',
                          opacity: 0.7,
                        }}
                      />
                    </span>
                  </h3>
                </div>

                <div className="speaking-result-cefr-content-grid">
                  {/* CEFR Level Badge */}
                  <div className="speaking-result-cefr-level-section">
                    <div
                      className="speaking-result-cefr-level-badge"
                      style={{ background: cefrLevel.gradient }}
                    >
                      <span className="speaking-result-cefr-level-code">
                        {cefrLevel.code}
                      </span>
                      <span className="speaking-result-cefr-level-name">
                        {cefrLevel.name}
                      </span>
                    </div>
                    <p className="speaking-result-cefr-level-description">
                      {cefrLevel.description}
                    </p>
                  </div>

                  {/* Graduate Program Eligibility */}
                  <div className="speaking-result-cefr-admission-section">
                    <div
                      className={`speaking-result-admission-status ${admissionInfo.eligible ? 'eligible' : 'not-eligible'}`}
                    >
                      {admissionInfo.eligible ? (
                        <FiCheckCircle className="speaking-result-admission-icon" />
                      ) : (
                        <FiAlertCircle className="speaking-result-admission-icon" />
                      )}
                      <span className="speaking-result-admission-label">
                        {admissionInfo.eligible
                          ? 'Graduate Ready'
                          : 'Keep Improving'}
                      </span>
                    </div>
                    <p className="speaking-result-admission-message">
                      {admissionInfo.message}
                    </p>
                    <p className="speaking-result-admission-recommendation">
                      {admissionInfo.recommendation}
                    </p>

                    {admissionInfo.programs.length > 0 && (
                      <div className="speaking-result-admission-programs">
                        <strong>Eligible Programs:</strong>
                        <ul>
                          {admissionInfo.programs.map((program, idx) => (
                            <li key={idx}>{program}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>

                {/* Next Level Info */}
                {nextLevel && (
                  <div className="speaking-result-cefr-next-level">
                    <div className="speaking-result-next-level-header">
                      <FiTarget className="speaking-result-next-level-icon" />
                      <span>Next Goal: {formatCEFRDisplay(nextLevel)}</span>
                    </div>
                    {daysToC1 > 0 &&
                      cefrLevel.code !== 'C1' &&
                      cefrLevel.code !== 'C2' && (
                        <p className="speaking-result-days-to-target">
                          <strong>{daysToC1} days</strong> of daily practice to
                          reach C1 (Graduate-ready)
                        </p>
                      )}
                    {isEnrolled ? (
                      <>
                        <button
                          className="btn-lwz-primary speaking-result-start-journey-btn"
                          onClick={() => navigate('/exams/english/weekly-review')}
                        >
                          <FiMap style={{ marginRight: '0.5rem' }} />
                          Finish weekly review
                        </button>
                        <button
                          className="btn-lwz-primary speaking-result-start-journey-btn"
                          onClick={() => navigate('/exams/english/journey')}
                        >
                          <FiMap style={{ marginRight: '0.5rem' }} />
                          View My Learning Journey
                        </button>
                      </>
                    ) : (
                      <button
                        className="btn-lwz-primary speaking-result-start-journey-btn"
                        onClick={() => setShowEnrollmentModal(true)}
                      >
                        <FiMap style={{ marginRight: '0.5rem' }} />
                        Start Your Learning Journey
                      </button>
                    )}
                  </div>
                )}

                {/* CEFR Scale Reference */}
                <div className="speaking-result-cefr-scale-reference">
                  <span className="speaking-result-scale-label">
                    CEFR Scale:
                  </span>
                  <div className="speaking-result-cefr-scale-bar">
                    {['A1', 'A2', 'B1', 'B2', 'C1', 'C2'].map((level) => (
                      <div
                        key={level}
                        className={`speaking-result-scale-segment ${level === cefrLevel.code ? 'active' : ''} ${['B2', 'C1'].includes(level) ? 'graduate-ready' : ''}`}
                        title={`${level}${['B2', 'C1'].includes(level) ? ' - Graduate Programs' : ''}`}
                      >
                        {level}
                      </div>
                    ))}
                  </div>
                  <div className="speaking-result-graduate-requirement-note">
                    <FiInfo style={{ marginRight: '0.25rem' }} />
                    Graduate programs typically require{' '}
                    <strong>B2 to C1</strong> (IELTS 5.5-8)
                  </div>
                </div>
              </div>
            );
          })()}

          <ToeflNextStepCard bandScore={assessment?.overallBandScore} />

          {/* Progress Comparison (only show for previous attempts) */}
          {allAttempts.length > 1 && selectedAttemptIndex > 0 && (
            <div className="speaking-result-progress-comparison">
              <h3>
                <FiTrendingUp style={{ marginRight: '0.5rem' }} /> Progress
                Comparison
                <span
                  className="speaking-result-score-info-tooltip"
                  title="Progress Comparison: Compare your performance between different attempts on the same topic. See how your scores have changed over time and identify areas of improvement."
                >
                  <FiHelpCircle
                    style={{
                      marginLeft: '0.5rem',
                      cursor: 'pointer',
                      opacity: 0.7,
                    }}
                  />
                </span>
              </h3>
              {(() => {
                const currentAttempt = allAttempts[selectedAttemptIndex];
                const latestAttempt = allAttempts[0];
                const currentAssessment = getAssessment(currentAttempt.results);
                const latestAssessment = getAssessment(latestAttempt.results);

                if (!currentAssessment || !latestAssessment) {
                  return (
                    <div className="speaking-result-comparison-grid">
                      <div className="speaking-result-comparison-item">
                        <div className="speaking-result-comparison-label">
                          Comparison Unavailable
                        </div>
                        <div className="speaking-result-comparison-value neutral">
                          Data structure inconsistency
                        </div>
                      </div>
                    </div>
                  );
                }

                const scoreDiff =
                  latestAssessment.overallBandScore -
                  currentAssessment.overallBandScore;
                const fluencyDiff =
                  latestAssessment.fluencyAndCoherence -
                  currentAssessment.fluencyAndCoherence;
                const vocabularyDiff =
                  latestAssessment.lexicalResource -
                  currentAssessment.lexicalResource;
                const grammarDiff =
                  latestAssessment.grammaticalRangeAndAccuracy -
                  currentAssessment.grammaticalRangeAndAccuracy;
                const pronunciationDiff =
                  latestAssessment.pronunciation -
                  currentAssessment.pronunciation;

                return (
                  <div className="speaking-result-comparison-grid">
                    <div className="speaking-result-comparison-item">
                      <div className="speaking-result-comparison-label">
                        Overall Score Change
                        <span
                          className="speaking-result-score-info-tooltip"
                          title="Overall Score Change: Shows the difference between your latest attempt and this attempt. A positive number means improvement, negative means decline, and 0 means no change."
                        >
                          <FiHelpCircle
                            style={{
                              marginLeft: '0.5rem',
                              cursor: 'pointer',
                              opacity: 0.7,
                            }}
                          />
                        </span>
                      </div>
                      <div
                        className={`speaking-result-comparison-value ${scoreDiff > 0 ? 'positive' : scoreDiff < 0 ? 'negative' : 'neutral'}`}
                      >
                        {scoreDiff > 0 ? '+' : ''}
                        {scoreDiff.toFixed(1)}
                      </div>
                    </div>
                    <div className="speaking-result-comparison-item">
                      <div className="speaking-result-comparison-label">
                        Fluency Change
                        <span
                          className="speaking-result-score-info-tooltip"
                          title="Fluency Change: Compares your fluency and coherence scores between attempts. Shows progress in speaking smoothly and connecting ideas logically."
                        >
                          <FiHelpCircle
                            style={{
                              marginLeft: '0.5rem',
                              cursor: 'pointer',
                              opacity: 0.7,
                            }}
                          />
                        </span>
                      </div>
                      <div
                        className={`speaking-result-comparison-value ${fluencyDiff > 0 ? 'positive' : fluencyDiff < 0 ? 'negative' : 'neutral'}`}
                      >
                        {fluencyDiff > 0 ? '+' : ''}
                        {fluencyDiff.toFixed(1)}
                      </div>
                    </div>
                    <div className="speaking-result-comparison-item">
                      <div className="speaking-result-comparison-label">
                        Vocabulary Change
                        <span
                          className="speaking-result-score-info-tooltip"
                          title="Vocabulary Change: Compares your lexical resource scores between attempts. Shows progress in using varied and appropriate vocabulary."
                        >
                          <FiHelpCircle
                            style={{
                              marginLeft: '0.5rem',
                              cursor: 'pointer',
                              opacity: 0.7,
                            }}
                          />
                        </span>
                      </div>
                      <div
                        className={`speaking-result-comparison-value ${vocabularyDiff > 0 ? 'positive' : vocabularyDiff < 0 ? 'negative' : 'neutral'}`}
                      >
                        {vocabularyDiff > 0 ? '+' : ''}
                        {vocabularyDiff.toFixed(1)}
                      </div>
                    </div>
                    <div className="speaking-result-comparison-item">
                      <div className="speaking-result-comparison-label">
                        Grammar Change
                        <span
                          className="speaking-result-score-info-tooltip"
                          title="Grammar Change: Compares your grammatical range and accuracy scores between attempts. Shows progress in using correct grammar and varied sentence structures."
                        >
                          <FiHelpCircle
                            style={{
                              marginLeft: '0.5rem',
                              cursor: 'pointer',
                              opacity: 0.7,
                            }}
                          />
                        </span>
                      </div>
                      <div
                        className={`speaking-result-comparison-value ${grammarDiff > 0 ? 'positive' : grammarDiff < 0 ? 'negative' : 'neutral'}`}
                      >
                        {grammarDiff > 0 ? '+' : ''}
                        {grammarDiff.toFixed(1)}
                      </div>
                    </div>
                    <div className="speaking-result-comparison-item">
                      <div className="speaking-result-comparison-label">
                        Pronunciation Change
                        <span
                          className="speaking-result-score-info-tooltip"
                          title="Pronunciation Change: Compares your pronunciation scores between attempts. Shows progress in speech clarity, stress patterns, and intonation."
                        >
                          <FiHelpCircle
                            style={{
                              marginLeft: '0.5rem',
                              cursor: 'pointer',
                              opacity: 0.7,
                            }}
                          />
                        </span>
                      </div>
                      <div
                        className={`speaking-result-comparison-value ${pronunciationDiff > 0 ? 'positive' : pronunciationDiff < 0 ? 'negative' : 'neutral'}`}
                      >
                        {pronunciationDiff > 0 ? '+' : ''}
                        {pronunciationDiff.toFixed(1)}
                      </div>
                    </div>
                    <div className="speaking-result-comparison-item">
                      <div className="speaking-result-comparison-label">
                        Latest Attempt
                      </div>
                      <div className="speaking-result-comparison-value neutral">
                        {new Date(
                          latestAttempt.completedAt
                        ).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Detailed Scores Card */}
          <div className="speaking-result-detailed-scores">
            <h3 className="speaking-result-section-title">
              Detailed Assessment
            </h3>
            <div className="speaking-result-scores-grid">
              {[
                {
                  label: 'Fluency and Coherence',
                  score: assessment?.fluencyAndCoherence,
                  icon: <FiMessageSquare />,
                  tooltip:
                    'Fluency & Coherence (1-9): Liwaza AI evaluates how smoothly you speak and how well your ideas connect. This includes speaking without long pauses, using linking words, organizing thoughts logically, and maintaining conversation flow naturally.',
                },
                {
                  label: 'Lexical Resource',
                  score: assessment?.lexicalResource,
                  icon: <FiBookOpen />,
                  tooltip:
                    'Lexical Resource (1-9): Liwaza AI assesses your vocabulary range and accuracy. This includes using varied and appropriate words, avoiding repetition, using collocations correctly, and demonstrating awareness of word forms and meanings.',
                },
                {
                  label: 'Grammatical Range and Accuracy',
                  score: assessment?.grammaticalRangeAndAccuracy,
                  icon: <FiEdit3 />,
                  tooltip:
                    'Grammatical Range & Accuracy (1-9): Liwaza AI evaluates your grammar usage and sentence structure. This includes using various sentence types, correct verb tenses, appropriate articles, and maintaining grammatical accuracy while speaking naturally.',
                },
                {
                  label: 'Pronunciation',
                  score: assessment?.pronunciation,
                  icon: <FiMusic />,
                  tooltip:
                    'Pronunciation (1-9): Liwaza AI assesses your speech clarity and accent. This includes clear articulation of sounds, correct stress patterns, appropriate intonation, and overall comprehensibility of your spoken English.',
                },
              ].map((item, index) => (
                <div key={index} className="speaking-result-score-item">
                  <div className="speaking-result-score-item-header">
                    <span className="speaking-result-score-icon">
                      {item.icon}
                    </span>
                    <span className="speaking-result-score-label">
                      {item.label}
                      <span
                        className="speaking-result-score-info-tooltip"
                        title={item.tooltip}
                      >
                        <FiHelpCircle
                          style={{
                            marginLeft: '0.5rem',
                            cursor: 'pointer',
                            opacity: 0.7,
                          }}
                        />
                      </span>
                    </span>
                  </div>
                  <div
                    className={`speaking-result-score-value ${getScoreColor(item.score)}`}
                  >
                    {item.score}/9
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Areas for Improvement Card */}
          <div className="speaking-result-improvement-card">
            <h3 className="speaking-result-section-title">
              <FiBarChart style={{ marginRight: '0.5rem' }} /> Action Items for
              Growth
              <span
                className="speaking-result-score-info-tooltip"
                title="Action Items for Growth: Specific areas where you can improve your speaking skills. These are personalized recommendations based on your performance analysis to help you achieve higher band scores."
              >
                <FiHelpCircle
                  style={{
                    marginLeft: '0.5rem',
                    cursor: 'pointer',
                    opacity: 0.7,
                  }}
                />
              </span>
            </h3>
            <div className="speaking-result-improvement-list">
              {getAreasForImprovement(results).map((area, index) => (
                <div key={index} className="speaking-result-improvement-item">
                  <div className="speaking-result-improvement-number">
                    {index + 1}
                  </div>
                  <div className="speaking-result-improvement-text">
                    <ReactMarkdown>{area}</ReactMarkdown>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Encouragement Card */}
          <div className="speaking-result-encouragement-card">
            <div className="speaking-result-encouragement-header">
              <h3>
                <FiStar style={{ marginRight: '0.5rem' }} /> Keep Up the Great
                Work!
                <span
                  className="speaking-result-score-info-tooltip"
                  title="Keep Up the Great Work: Motivational feedback to encourage continued practice. This section recognizes your achievements and provides positive reinforcement for your learning journey."
                >
                  <FiHelpCircle
                    style={{
                      marginLeft: '0.5rem',
                      cursor: 'pointer',
                      opacity: 0.7,
                    }}
                  />
                </span>
              </h3>
            </div>
            <p className="speaking-result-encouragement-text">
              {getEncouragement(results)}
            </p>
          </div>

          {/* Examiner Debrief Card - only shown in exam mode */}
          {getExaminerDebrief(results) && (
            <div className="speaking-result-examiner-debrief-card">
              <h3 className="speaking-result-section-title">
                <FiEdit3 style={{ marginRight: '0.5rem' }} /> Examiner Debrief
                <span
                  className="speaking-result-score-info-tooltip"
                  title="Examiner Debrief: A formal, detailed assessment from your IELTS examiner covering your strengths, weaknesses, and specific recommendations for improvement. This mirrors the feedback you would receive in a real IELTS examination."
                >
                  <FiHelpCircle
                    style={{
                      marginLeft: '0.5rem',
                      cursor: 'pointer',
                      opacity: 0.7,
                    }}
                  />
                </span>
              </h3>
              <div className="speaking-result-debrief-content">
                {getExaminerDebrief(results)
                  .split('\n')
                  .filter((p) => p.trim())
                  .map((paragraph, idx) => (
                    <p key={idx}>{paragraph}</p>
                  ))}
              </div>
            </div>
          )}

          {/* Problematic Words Card */}
          {getProblematicWords(results).length > 0 && (
            <div className="speaking-result-problematic-words-card">
              <h3 className="speaking-result-section-title">
                <FiBookOpen
                  style={{ marginRight: '0.5rem', color: '#ef4444' }}
                />{' '}
                Words to Work On
                <span className="speaking-result-word-count-badge">
                  {getProblematicWords(results).length}
                </span>
                <span
                  className="speaking-result-score-info-tooltip"
                  title="Words to Work On: These are specific words or phrases you used incorrectly or could improve. Each word includes its correct form, pronunciation guide, definition, example sentences, and targeted practice exercises."
                >
                  <FiHelpCircle
                    style={{
                      marginLeft: '0.5rem',
                      cursor: 'pointer',
                      opacity: 0.7,
                    }}
                  />
                </span>
              </h3>
              <p className="speaking-result-words-intro">
                Here are words you used incorrectly or could improve, with
                pronunciation guides, examples, and practice exercises.
              </p>
              <div className="speaking-result-problematic-words-list">
                {getProblematicWords(results).map((wordItem, idx) => {
                  // Find matching exercise for this word
                  const matchingExercise = getMissedWordsActions(results).find(
                    (a) =>
                      a.word?.toLowerCase() === wordItem.word?.toLowerCase()
                  );
                  return (
                    <div key={idx} className="speaking-result-word-card">
                      <div className="speaking-result-word-card-number">
                        {idx + 1}
                      </div>
                      <div className="speaking-result-word-header">
                        <span className="speaking-result-word-title">
                          {wordItem.word}
                        </span>
                        <button
                          className="pronounce-word-btn"
                          onClick={() => playSpeakingWord(wordItem.word)}
                          title={`Hear pronunciation of "${extractPronounceLemma(wordItem.word) || wordItem.word}"`}
                          aria-label={`Pronounce ${extractPronounceLemma(wordItem.word) || wordItem.word}`}
                        >
                          <FiVolume2 />
                        </button>
                        {resolvedSpeakingPhonetic(wordItem) && (
                          <span className="speaking-result-word-phonetic">
                            {resolvedSpeakingPhonetic(wordItem)}
                          </span>
                        )}
                        {wordItem.partOfSpeech && (
                          <span className="speaking-result-word-pos">
                            {wordItem.partOfSpeech}
                          </span>
                        )}
                      </div>
                      {wordItem.definition && (
                        <p className="speaking-result-word-definition">
                          {renderNuanceInlineMarkdown(wordItem.definition)}
                        </p>
                      )}
                      {wordItem.howMisused && (
                        <div className="speaking-result-word-misuse">
                          <FiAlertCircle
                            style={{ flexShrink: 0, marginTop: '2px' }}
                          />
                          <span>
                            {renderNuanceInlineMarkdown(wordItem.howMisused)}
                          </span>
                        </div>
                      )}
                      {wordItem.whyDifficult && (
                        <div className="speaking-result-word-difficulty">
                          <FiInfo style={{ flexShrink: 0, marginTop: '2px' }} />
                          <span>
                            {renderNuanceInlineMarkdown(wordItem.whyDifficult)}
                          </span>
                        </div>
                      )}
                      {wordItem.allForms && wordItem.allForms.length > 0 && (
                        <div className="speaking-result-word-forms">
                          <span className="speaking-result-forms-label">
                            Forms:
                          </span>
                          <div className="speaking-result-forms-tags">
                            {wordItem.allForms.map((form, fIdx) => (
                              <span
                                key={fIdx}
                                className="speaking-result-form-tag"
                              >
                                {form}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {wordItem.sampleSentences &&
                        wordItem.sampleSentences.length > 0 && (
                          <div className="speaking-result-word-sentences">
                            <span className="speaking-result-sentences-label">
                              Examples:
                            </span>
                            <ul>
                              {wordItem.sampleSentences.map(
                                (sentence, sIdx) => (
                                  <li key={sIdx}>
                                    {renderNuanceInlineMarkdown(sentence)}
                                  </li>
                                )
                              )}
                            </ul>
                          </div>
                        )}
                      {wordItem.pronunciationTip && (
                        <div className="speaking-result-word-pronunciation-tip">
                          <FiMusic
                            style={{ flexShrink: 0, marginTop: '2px' }}
                          />
                          <span>
                            {renderNuanceInlineMarkdown(
                              wordItem.pronunciationTip
                            )}
                          </span>
                        </div>
                      )}
                      {/* Inline Practice Exercise for this word */}
                      {matchingExercise && (
                        <div className="speaking-result-word-exercise">
                          <div className="speaking-result-word-exercise-header">
                            <FiTarget
                              style={{ flexShrink: 0, color: '#16a34a' }}
                            />
                            <span className="speaking-result-word-exercise-label">
                              Practice
                            </span>
                            {matchingExercise.actionType && (
                              <span className="speaking-result-exercise-type">
                                {matchingExercise.actionType.replace(/_/g, ' ')}
                              </span>
                            )}
                          </div>
                          {matchingExercise.description && (
                            <p className="speaking-result-word-exercise-desc">
                              {renderNuanceInlineMarkdown(
                                matchingExercise.description
                              )}
                            </p>
                          )}
                          {matchingExercise.exercises &&
                            matchingExercise.exercises.length > 0 && (
                              <ul className="speaking-result-exercise-steps">
                                {matchingExercise.exercises.map(
                                  (exercise, eIdx) => (
                                    <li key={eIdx}>
                                      {renderNuanceInlineMarkdown(exercise)}
                                    </li>
                                  )
                                )}
                              </ul>
                            )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {/* Show any unmatched exercises that don't correspond to a word card */}
              {getMissedWordsActions(results).filter(
                (a) =>
                  !getProblematicWords(results).some(
                    (w) => w.word?.toLowerCase() === a.word?.toLowerCase()
                  )
              ).length > 0 && (
                <div className="speaking-result-extra-exercises">
                  <h4 className="speaking-result-extra-exercises-title">
                    <FiTarget
                      style={{ marginRight: '0.4rem', color: '#16a34a' }}
                    />
                    Additional Practice
                  </h4>
                  <div className="speaking-result-exercises-list">
                    {getMissedWordsActions(results)
                      .filter(
                        (a) =>
                          !getProblematicWords(results).some(
                            (w) =>
                              w.word?.toLowerCase() === a.word?.toLowerCase()
                          )
                      )
                      .map((action, idx) => (
                        <div
                          key={idx}
                          className="speaking-result-exercise-card"
                        >
                          <div className="speaking-result-exercise-header">
                            <span className="speaking-result-exercise-word">
                              {action.word}
                            </span>
                            <button
                              className="pronounce-word-btn"
                              onClick={() => playSpeakingWord(action.word)}
                              title={`Hear pronunciation of "${action.word}"`}
                              aria-label={`Pronounce ${action.word}`}
                            >
                              <FiVolume2 />
                            </button>
                            {action.actionType && (
                              <span className="speaking-result-exercise-type">
                                {action.actionType.replace(/_/g, ' ')}
                              </span>
                            )}
                          </div>
                          <p className="speaking-result-exercise-description">
                            {renderNuanceInlineMarkdown(action.description)}
                          </p>
                          {action.exercises && action.exercises.length > 0 && (
                            <ul className="speaking-result-exercise-steps">
                              {action.exercises.map((exercise, eIdx) => (
                                <li key={eIdx}>
                                  {renderNuanceInlineMarkdown(exercise)}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Standalone Practice Exercises - only if no problematic words but exercises exist */}
          {getProblematicWords(results).length === 0 &&
            getMissedWordsActions(results).length > 0 && (
              <div className="speaking-result-practice-exercises-card">
                <h3 className="speaking-result-section-title">
                  <FiTarget
                    style={{ marginRight: '0.5rem', color: '#16a34a' }}
                  />{' '}
                  Practice Exercises
                  <span
                    className="speaking-result-score-info-tooltip"
                    title="Practice Exercises: Targeted exercises to strengthen your vocabulary and word usage."
                  >
                    <FiHelpCircle
                      style={{
                        marginLeft: '0.5rem',
                        cursor: 'pointer',
                        opacity: 0.7,
                      }}
                    />
                  </span>
                </h3>
                <div className="speaking-result-exercises-list">
                  {getMissedWordsActions(results).map((action, idx) => (
                    <div key={idx} className="speaking-result-exercise-card">
                      <div className="speaking-result-exercise-header">
                        <span className="speaking-result-exercise-word">
                          {action.word}
                        </span>
                        <button
                          className="pronounce-word-btn"
                          onClick={() => playSpeakingWord(action.word)}
                          title={`Hear pronunciation of "${action.word}"`}
                          aria-label={`Pronounce ${action.word}`}
                        >
                          <FiVolume2 />
                        </button>
                        {action.actionType && (
                          <span className="speaking-result-exercise-type">
                            {action.actionType.replace(/_/g, ' ')}
                          </span>
                        )}
                      </div>
                      <p className="speaking-result-exercise-description">
                        {renderNuanceInlineMarkdown(action.description)}
                      </p>
                      {action.exercises && action.exercises.length > 0 && (
                        <ul className="speaking-result-exercise-steps">
                          {action.exercises.map((exercise, eIdx) => (
                            <li key={eIdx}>
                              {renderNuanceInlineMarkdown(exercise)}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

          {/* Action Buttons after encouragement */}
          <div className="speaking-result-main-actions">
            <button
              className="btn-lwz-secondary speaking-result-main-action-button"
              onClick={handleShare}
            >
              <FiShare2 />
              <span>Share Results</span>
            </button>
            <button
              className="btn-lwz-primary speaking-result-main-action-button"
              onClick={handleNavigateToSpeaking}
            >
              <FiMic />
              <span>More Speaking Tests</span>
            </button>
          </div>

          {/* Previous Attempts Card */}
          {allAttempts.length > 1 && (
            <div className="speaking-result-previous-attempts">
              <h3 className="speaking-result-section-title">
                <FiBarChart style={{ marginRight: '0.5rem' }} /> All Attempts
                for "{resultData.topic}"
                <span
                  className="speaking-result-score-info-tooltip"
                  title="All Attempts: View and compare all your previous attempts on this topic. Click on any attempt to see detailed results and track your progress over time."
                >
                  <FiHelpCircle
                    style={{
                      marginLeft: '0.5rem',
                      cursor: 'pointer',
                      opacity: 0.7,
                    }}
                  />
                </span>
              </h3>
              <div className="speaking-result-attempts-grid">
                {allAttempts.map((attempt, index) => {
                  const attemptAssessment = getAssessment(attempt.results);

                  return (
                    <div
                      key={index}
                      className={`speaking-result-attempt-card ${index === selectedAttemptIndex ? 'active' : ''}`}
                      onClick={() => handleAttemptChange(index)}
                    >
                      <div className="speaking-result-attempt-header">
                        <div className="speaking-result-attempt-number">
                          Attempt {allAttempts.length - index}
                          {index === 0 && (
                            <span className="speaking-result-latest-badge">
                              Latest
                            </span>
                          )}
                        </div>
                        <div
                          className={`speaking-result-attempt-score ${getScoreColor(attemptAssessment?.overallBandScore)}`}
                        >
                          {attemptAssessment?.overallBandScore || 'N/A'}/9
                        </div>
                      </div>
                      <div className="speaking-result-attempt-details">
                        <div className="speaking-result-attempt-date">
                          {formatDate(attempt.completedAt)}
                        </div>
                        <div className="speaking-result-attempt-scores">
                          <div className="speaking-result-score-mini">
                            <span>F:</span>{' '}
                            {attemptAssessment?.fluencyAndCoherence || 'N/A'}
                          </div>
                          <div className="speaking-result-score-mini">
                            <span>V:</span>{' '}
                            {attemptAssessment?.lexicalResource || 'N/A'}
                          </div>
                          <div className="speaking-result-score-mini">
                            <span>G:</span>{' '}
                            {attemptAssessment?.grammaticalRangeAndAccuracy ||
                              'N/A'}
                          </div>
                          <div className="speaking-result-score-mini">
                            <span>P:</span>{' '}
                            {attemptAssessment?.pronunciation || 'N/A'}
                          </div>
                        </div>
                        <div className="speaking-result-attempt-duration">
                          Duration: {formatDuration(attempt.duration)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Get Started With Component */}
          <div style={{ marginTop: '2rem', marginBottom: '2rem' }}>
            <GetStartedWith
              title="Need help improving your IELTS Speaking performance?"
              spaceName="IELTS Speaking"
              humanCoachTitle="Talk to an IELTS coach"
              liwazaAITitle="Ask AI"
              communityTitle="Ask other students"
              showHumanCoach={true}
              showLiwazaAI={true}
              showCommunity={true}
              aifirstmessage={`I just completed an IELTS Speaking test and got a band score of ${assessment?.overallBandScore || 'N/A'}. I need help improving my speaking performance.`}
              aicontext={`IELTS Speaking Results: Overall Band Score: ${assessment?.overallBandScore || 'N/A'}/9, Fluency and Coherence: ${assessment?.fluencyAndCoherence || 'N/A'}/9, Lexical Resource: ${assessment?.lexicalResource || 'N/A'}/9, Grammatical Range and Accuracy: ${assessment?.grammaticalRangeAndAccuracy || 'N/A'}/9, Pronunciation: ${assessment?.pronunciation || 'N/A'}/9. Topic: ${topic}. Duration: ${formatDuration(duration)}.`}
            />
          </div>

          {/* Daily Practice CTA Card */}
          <div className="speaking-result-practice-cta">
            <div className="speaking-result-cta-content">
              <div className="speaking-result-cta-header">
                <span className="speaking-result-cta-icon">
                  <FiTarget />
                </span>
                <h3>
                  Ready for tomorrow's practice?
                  <span
                    className="speaking-result-score-info-tooltip"
                    title="Daily Practice Tip: Practical advice to help you maintain consistent practice habits. These tips are designed to support your ongoing improvement and keep you motivated for daily practice sessions."
                  >
                    <FiHelpCircle
                      style={{
                        marginLeft: '0.5rem',
                        cursor: 'pointer',
                        opacity: 0.7,
                      }}
                    />
                  </span>
                </h3>
              </div>
              <p className="speaking-result-cta-message">
                {getDailyPracticeMessage(results)}
              </p>
            </div>
          </div>

          {/* Additional Action Buttons */}
          <div className="speaking-result-action-grid">
            <button
              className="btn-lwz-secondary speaking-result-action-button"
              onClick={handleViewProgress}
            >
              <FiActivity />
              <span>My Progress</span>
            </button>
          </div>
        </div>

        {/* Enrollment Modal */}
        {showEnrollmentModal && (
          <div
            className="speaking-result-enrollment-modal-overlay"
            onClick={handleSkipEnrollment}
          >
            <div
              className="speaking-result-enrollment-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="speaking-result-enrollment-modal-close"
                onClick={handleSkipEnrollment}
              >
                <FiX />
              </button>

              <div className="speaking-result-enrollment-modal-header">
                <div className="speaking-result-enrollment-icon-wrapper">
                  <FiMap className="speaking-result-enrollment-icon" />
                </div>
                <h2>Start Your Personalized Learning Journey</h2>
                <p className="speaking-result-enrollment-subtitle">
                  Based on your speaking assessment, we've created a customized
                  path to help you reach graduate-level English proficiency.
                </p>
              </div>

              <div className="speaking-result-enrollment-modal-body">
                <div className="speaking-result-enrollment-level-info">
                  {(() => {
                    const assessment = getAssessment(resultData?.results);
                    const cefrLevel = getCEFRFromIELTS(
                      assessment?.overallBandScore
                    );
                    const daysToC1 = getDaysToTarget(cefrLevel.code, 'C1');

                    return (
                      <>
                        <div className="speaking-result-current-level-display">
                          <span className="speaking-result-level-label">
                            Your Current Level
                          </span>
                          <span
                            className="speaking-result-level-badge"
                            style={{ background: cefrLevel.gradient }}
                          >
                            {cefrLevel.code}
                          </span>
                          <span className="speaking-result-level-name">
                            {cefrLevel.name}
                          </span>
                        </div>
                        {daysToC1 > 0 &&
                          cefrLevel.code !== 'C1' &&
                          cefrLevel.code !== 'C2' && (
                            <div className="speaking-result-target-info">
                              <FiTarget className="speaking-result-target-icon" />
                              <span>
                                <strong>{daysToC1} days</strong> to reach C1
                                (Graduate-ready)
                              </span>
                            </div>
                          )}
                      </>
                    );
                  })()}
                </div>

                <div className="speaking-result-enrollment-commitment">
                  <h3>
                    <FiClock style={{ marginRight: '0.5rem' }} />
                    Daily Commitment
                  </h3>
                  <p className="speaking-result-commitment-intro">
                    To progress effectively, you'll need to dedicate{' '}
                    <strong>30 minutes per day</strong> to your English
                    improvement:
                  </p>
                  <div className="speaking-result-commitment-breakdown">
                    <div className="speaking-result-commitment-item">
                      <div className="speaking-result-commitment-time">
                        10 mins
                      </div>
                      <div className="speaking-result-commitment-activity">
                        <FiMic className="speaking-result-commitment-icon" />
                        <span>Speaking Practice with AI Coach</span>
                      </div>
                    </div>
                    <div className="speaking-result-commitment-item">
                      <div className="speaking-result-commitment-time">
                        20 mins
                      </div>
                      <div className="speaking-result-commitment-activity">
                        <FiBookOpen className="speaking-result-commitment-icon" />
                        <span>Grammar & Vocabulary Courses</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="speaking-result-enrollment-benefits">
                  <h3>What You'll Get</h3>
                  <ul>
                    <li>
                      <FiCheckCircle className="speaking-result-benefit-icon" />
                      <span>Visual daily roadmap with clickable tasks</span>
                    </li>
                    <li>
                      <FiCheckCircle className="speaking-result-benefit-icon" />
                      <span>
                        Weekly checkpoint tests to track your progress
                      </span>
                    </li>
                    <li>
                      <FiCheckCircle className="speaking-result-benefit-icon" />
                      <span>
                        Grammar and vocabulary courses tailored to your level
                      </span>
                    </li>
                    <li>
                      <FiCheckCircle className="speaking-result-benefit-icon" />
                      <span>Daily speaking sessions with AI feedback</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="speaking-result-enrollment-modal-footer">
                <button
                  className="speaking-result-btn-enroll-skip"
                  onClick={handleSkipEnrollment}
                >
                  Maybe Later
                </button>
                <button
                  className="speaking-result-btn-enroll-confirm"
                  onClick={handleEnroll}
                >
                  <FiCalendar style={{ marginRight: '0.5rem' }} />
                  Start My Journey
                </button>
              </div>

              <p className="speaking-result-enrollment-note">
                <FiInfo style={{ marginRight: '0.25rem' }} />
                You can always enroll later from the English learning page.
              </p>
            </div>
          </div>
        )}

        {/* Level Update Modal for Reassessments */}
        {showLevelUpdateModal && levelUpdateInfo && (
          <div
            className="speaking-result-enrollment-modal-overlay"
            onClick={() => setShowLevelUpdateModal(false)}
          >
            <div
              className="speaking-result-enrollment-modal speaking-result-level-update-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="speaking-result-enrollment-modal-close"
                onClick={() => setShowLevelUpdateModal(false)}
              >
                <FiX />
              </button>

              <div className="speaking-result-enrollment-modal-header">
                <div className="speaking-result-enrollment-icon-wrapper">
                  {levelUpdateInfo.isUpgrade ? (
                    <FiTrendingUp className="speaking-result-enrollment-icon level-up-icon" />
                  ) : (
                    <FiRefreshCw className="speaking-result-enrollment-icon" />
                  )}
                </div>
                <h2>
                  {levelUpdateInfo.isUpgrade
                    ? "🎉 Congratulations! You've Leveled Up!"
                    : 'Update Your Learning Journey'}
                </h2>
                <p className="speaking-result-enrollment-subtitle">
                  {levelUpdateInfo.isUpgrade
                    ? 'Your speaking skills have improved! Update your journey to match your new level.'
                    : 'Your reassessment shows a different level. Would you like to update your journey?'}
                </p>
              </div>

              <div className="speaking-result-enrollment-modal-body">
                <div className="speaking-result-level-comparison">
                  <div className="speaking-result-level-box">
                    <span className="speaking-result-level-label">
                      Previous Level
                    </span>
                    <span
                      className="speaking-result-level-badge"
                      style={{
                        background: getCEFRFromIELTS(
                          levelUpdateInfo.currentLevel === 'A1'
                            ? 1
                            : levelUpdateInfo.currentLevel === 'A2'
                              ? 3
                              : levelUpdateInfo.currentLevel === 'B1'
                                ? 4
                                : levelUpdateInfo.currentLevel === 'B2'
                                  ? 6
                                  : levelUpdateInfo.currentLevel === 'C1'
                                    ? 7
                                    : 9
                        ).gradient,
                      }}
                    >
                      {levelUpdateInfo.currentLevel}
                    </span>
                  </div>
                  <div className="speaking-result-level-arrow">
                    {levelUpdateInfo.isUpgrade ? '→' : '↔'}
                  </div>
                  <div className="speaking-result-level-box new-level">
                    <span className="speaking-result-level-label">
                      New Level
                    </span>
                    <span
                      className="speaking-result-level-badge"
                      style={{
                        background: getCEFRFromIELTS(levelUpdateInfo.newScore)
                          .gradient,
                      }}
                    >
                      {levelUpdateInfo.newLevel}
                    </span>
                  </div>
                </div>

                <div className="speaking-result-update-note">
                  <FiInfo />
                  <p>
                    Updating your journey will create a new personalized path
                    from <strong>{levelUpdateInfo.newLevel}</strong> to{' '}
                    <strong>{getNextLevel(levelUpdateInfo.newLevel)}</strong>.
                    Your previous progress will be saved in your history.
                  </p>
                </div>
              </div>

              <div className="speaking-result-enrollment-modal-footer">
                <button
                  className="speaking-result-btn-enroll-skip"
                  onClick={() => setShowLevelUpdateModal(false)}
                >
                  Keep Current Journey
                </button>
                <button
                  className="speaking-result-btn-enroll-confirm"
                  onClick={handleLevelUpdate}
                >
                  <FiRefreshCw style={{ marginRight: '0.5rem' }} />
                  Update My Journey
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Retake Confirmation Modal */}
        {showRetakeConfirmModal && (
          <div
            className="speaking-result-enrollment-modal-overlay"
            onClick={() => setShowRetakeConfirmModal(false)}
          >
            <div
              className="speaking-result-enrollment-modal retake-confirm-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="speaking-result-modal-close"
                onClick={() => setShowRetakeConfirmModal(false)}
              >
                <FiX />
              </button>

              <div className="speaking-result-enrollment-modal-header">
                <div className="speaking-result-enrollment-modal-icon retake-icon">
                  <FiRefreshCw />
                </div>
                <h2>
                  {isIELTSTest
                    ? 'Retake IELTS Speaking Test?'
                    : 'Retake This Test?'}
                </h2>
                <p className="speaking-result-enrollment-subtitle">
                  You can retake this test one more time
                </p>
              </div>

              <div className="speaking-result-enrollment-modal-body">
                <div className="retake-warning-card">
                  <FiClock className="retake-warning-icon" />
                  <div className="retake-warning-content">
                    <h4>Voice Minutes Notice</h4>
                    <p>
                      This retake will be counted against your{' '}
                      <strong>weekly voice minutes</strong> allocation.
                      {isIELTSTest
                        ? ' The IELTS speaking test is 11 minutes long.'
                        : ' Make sure you have enough minutes remaining.'}
                    </p>
                  </div>
                </div>

                <div className="retake-info-note">
                  <FiInfo />
                  <span>
                    You can only retake this specific test once. After that,
                    continue with your journey activities or practice other
                    topics.
                  </span>
                </div>
              </div>

              <div className="speaking-result-enrollment-modal-footer">
                <button
                  className="speaking-result-btn-enroll-skip"
                  onClick={() => setShowRetakeConfirmModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="speaking-result-btn-enroll-confirm"
                  onClick={handleRetakeConfirm}
                >
                  <FiRefreshCw style={{ marginRight: '0.5rem' }} />
                  Yes, Retake Test
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SpeakingResults;
